<template>
  <div>
    <v-tabs v-if="!isTeacher" v-model="tab" background-color="transparent" class="mb-4">
      <v-tab><v-icon left small>mdi-wrench</v-icon> Báo hỏng & Sửa chữa ({{ totalRepairs }})</v-tab>
      <v-tab><v-icon left small>mdi-calendar-clock</v-icon> Lịch bảo dưỡng định kỳ ({{ schedules.length }})</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- TAB 1: BÁO HỎNG & SỬA CHỮA -->
      <v-tab-item>
        <v-data-table
          :headers="computedRepairHeaders"
          :items="repairs"
          :loading="loading"
          item-key="id"
          no-data-text="Không có yêu cầu sửa chữa nào"
          :server-items-length="totalRepairs"
          :footer-props="{
            'items-per-page-text': 'Hiển thị mỗi trang',
            'items-per-page-all-text': 'Tất cả',
            'items-per-page-options': [10, 20, 50]
          }"
          @update:options="onOptionsChange"
        >
          <template v-slot:item.code="{ item }">
            <span class="font-weight-bold primary--text">{{ item.code }}</span>
          </template>

          <template v-slot:item.asset="{ item }">
            <span>{{ get(item, 'asset.name', '-') }}</span>
          </template>

          <template v-slot:item.brokenQuantity="{ item }">
            <v-chip x-small color="error" dark>{{ item.brokenQuantity || 1 }}</v-chip>
          </template>

          <template v-slot:item.priority="{ item }">
            <v-chip x-small :color="item.priority | priorityColor" dark>
              {{ item.priority | priorityLabel }}
            </v-chip>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip small :color="item.status | repairStatusColor" dark>
              {{ item.status | repairStatusLabel }}
            </v-chip>
          </template>

          <template v-slot:item.cost="{ item }">
            <span>{{ (item.repairCost || 0).toLocaleString('vi-VN') }} đ</span>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="!isTeacher && item.status !== 'COMPLETED' && item.status !== 'CANCELLED'"
              small
              color="success"
              text
              @click="onComplete(item)"
              title="Đánh dấu hoàn thành"
            >
              <v-icon small left>mdi-check-circle</v-icon> Hoàn tất
            </v-btn>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- TAB 2: LỊCH BẢO DƯỠNG (CHỈ ADMIN) -->
      <v-tab-item v-if="!isTeacher">
        <v-data-table
          :headers="scheduleHeaders"
          :items="schedules"
          item-key="id"
          no-data-text="Chưa có lịch bảo dưỡng nào"
        >
          <template v-slot:item.code="{ item }">
            <span class="font-weight-bold primary--text">{{ item.code }}</span>
          </template>

          <template v-slot:item.asset="{ item }">
            <span>{{ get(item, 'asset.name', '-') }}</span>
          </template>

          <template v-slot:item.frequency="{ item }">
            <span>{{ item.frequency | frequencyLabel }}</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip x-small :color="item.status | scheduleStatusColor" dark>
              {{ item.status | scheduleStatusLabel }}
            </v-chip>
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { get } from 'lodash'

const repairHeaders = [
  { text: 'Mã phiếu', value: 'code', align: 'left', sortable: false },
  { text: 'Thiết bị', value: 'asset', align: 'left', sortable: false },
  { text: 'SL hỏng', value: 'brokenQuantity', align: 'center', sortable: false },
  { text: 'Người báo', value: 'reporterName', align: 'left', sortable: false },
  { text: 'Hiện tượng hư hỏng', value: 'issueDescription', align: 'left', sortable: false },
  { text: 'Mức độ', value: 'priority', align: 'center', sortable: false },
  { text: 'Trạng thái', value: 'status', align: 'center', sortable: false },
  { text: 'Chi phí', value: 'cost', align: 'right', sortable: false },
  { text: 'Thao tác', value: 'actions', align: 'center', sortable: false, width: '130px' }
]

const scheduleHeaders = [
  { text: 'Mã lịch', value: 'code', align: 'left', sortable: false },
  { text: 'Tiêu đề bảo dưỡng', value: 'title', align: 'left', sortable: false },
  { text: 'Thiết bị áp dụng', value: 'asset', align: 'left', sortable: false },
  { text: 'Chu kỳ', value: 'frequency', align: 'center', sortable: false },
  { text: 'Hạn tiếp theo', value: 'nextDueDate', align: 'center', sortable: false },
  { text: 'Người thực hiện', value: 'performer', align: 'left', sortable: false },
  { text: 'Trạng thái', value: 'status', align: 'center', sortable: false }
]

export default {
  name: 'MaintenanceDataTable',
  filters: {
    priorityLabel(priority) {
      switch (priority) {
        case 'LOW': return 'Thấp'
        case 'MEDIUM': return 'Bình thường'
        case 'HIGH': return 'Cao'
        case 'URGENT': return 'Khẩn cấp'
        default: return priority
      }
    },
    priorityColor(priority) {
      switch (priority) {
        case 'LOW': return 'blue-grey'
        case 'MEDIUM': return 'primary'
        case 'HIGH': return 'warning'
        case 'URGENT': return 'error'
        default: return 'grey'
      }
    },
    repairStatusLabel(status) {
      switch (status) {
        case 'PENDING': return 'Chờ tiếp nhận'
        case 'IN_PROGRESS': return 'Đang sửa chữa'
        case 'COMPLETED': return 'Đã hoàn thành'
        case 'CANCELLED': return 'Đã hủy'
        default: return status
      }
    },
    repairStatusColor(status) {
      switch (status) {
        case 'PENDING': return 'warning'
        case 'IN_PROGRESS': return 'primary'
        case 'COMPLETED': return 'success'
        case 'CANCELLED': return 'blue-grey'
        default: return 'grey'
      }
    },
    frequencyLabel(f) {
      switch (f) {
        case 'MONTHLY': return 'Hàng tháng'
        case 'QUARTERLY': return 'Hàng quý (3 tháng)'
        case 'YEARLY': return 'Hàng năm'
        default: return f
      }
    },
    scheduleStatusLabel(s) {
      switch (s) {
        case 'UPCOMING': return 'Sắp tới'
        case 'OVERDUE': return 'Quá hạn'
        case 'COMPLETED': return 'Đã hoàn thành'
        default: return s
      }
    },
    scheduleStatusColor(s) {
      switch (s) {
        case 'UPCOMING': return 'indigo'
        case 'OVERDUE': return 'error'
        case 'COMPLETED': return 'success'
        default: return 'blue-grey'
      }
    }
  },
  data() {
    return {
      tab: 0,
      repairHeaders,
      scheduleHeaders
    }
  },
  computed: {
    ...mapState('FacilityMaintenance', ['repairs', 'schedules', 'totalRepairs', 'loading']),
    ...mapGetters('auth', ['role', 'user']),
    isTeacher() {
      return this.role && (this.role.name === 'Teacher' || this.role.type === 'teacher')
    },
    computedRepairHeaders() {
      if (this.isTeacher) {
        return this.repairHeaders.filter(h => h.value !== 'actions' && h.value !== 'cost')
      }
      return this.repairHeaders
    }
  },
  methods: {
    get,
    ...mapActions('FacilityMaintenance', ['requestPageSettings', 'searchRepairList', 'updateRepair']),
    async refresh(query) {
      const q = { ...query }
      if (this.isTeacher && this.user) {
        q.reporter = this.user.id
      }
      this.searchRepairList(q)
    },
    onOptionsChange({ page, itemsPerPage }) {
      this.requestPageSettings({ page, itemsPerPage })
    },
    async onComplete(item) {
      const costStr = prompt('Nhập chi phí sửa chữa thực tế (VNĐ):', item.repairCost || 0)
      const cost = parseFloat(costStr) || 0
      await this.updateRepair({
        id: item.id,
        status: 'COMPLETED',
        repairCost: cost,
        completionDate: new Date().toISOString().substring(0, 10)
      })
      this.refresh()
    }
  }
}
</script>
